import React from 'react';
import { Course, UserRole } from '../../types/lms.types';

interface OutcomesViewProps {
  type: 'course' | 'program';
  course: Course;
  userRole: UserRole;
  selectedModule: string;
}

const OutcomesView: React.FC<OutcomesViewProps> = ({ 
  type, 
  course, 
  userRole, 
  selectedModule 
}) => {
  // Mock course outcomes data
  const courseOutcomes = [
    {
      id: 'CO1',
      title: 'Mathematical Problem Solving',
      description: 'Students will demonstrate the ability to solve complex mathematical problems using algebraic methods and techniques.',
      moduleId: 'mod1',
      assessmentMethods: ['Assignments', 'Quizzes', 'Exams'],
      achievementLevel: 'High'
    },
    {
      id: 'CO2',
      title: 'Linear Systems Analysis',
      description: 'Students will be able to analyze and solve systems of linear equations using various mathematical approaches.',
      moduleId: 'mod2',
      assessmentMethods: ['Problem Sets', 'Projects', 'Midterm Exam'],
      achievementLevel: 'Medium'
    },
    {
      id: 'CO3',
      title: 'Quadratic Functions Mastery',
      description: 'Students will master the concepts of quadratic functions, including graphing, factoring, and real-world applications.',
      moduleId: 'mod3',
      assessmentMethods: ['Laboratory Work', 'Presentations', 'Final Exam'],
      achievementLevel: 'High'
    },
    {
      id: 'CO4',
      title: 'Mathematical Communication',
      description: 'Students will effectively communicate mathematical concepts and solutions both verbally and in writing.',
      moduleId: 'all',
      assessmentMethods: ['Presentations', 'Written Reports', 'Peer Reviews'],
      achievementLevel: 'Medium'
    }
  ];

  // Mock program outcomes data
  const programOutcomes = [
    {
      id: 'PO1',
      title: 'Critical Thinking and Problem Solving',
      description: 'Graduates will demonstrate critical thinking skills and the ability to solve complex problems systematically.',
      relatedCourses: ['Advanced Mathematics', 'Physics', 'Computer Science'],
      achievementLevel: 'High'
    },
    {
      id: 'PO2',
      title: 'Communication Skills',
      description: 'Graduates will communicate effectively in both oral and written formats to diverse audiences.',
      relatedCourses: ['Advanced Mathematics', 'Technical Writing', 'Presentation Skills'],
      achievementLevel: 'High'
    },
    {
      id: 'PO3',
      title: 'Analytical Reasoning',
      description: 'Graduates will apply analytical reasoning and quantitative methods to evaluate and solve problems.',
      relatedCourses: ['Advanced Mathematics', 'Statistics', 'Research Methods'],
      achievementLevel: 'Medium'
    },
    {
      id: 'PO4',
      title: 'Teamwork and Collaboration',
      description: 'Graduates will work effectively in teams and collaborate with diverse groups to achieve common goals.',
      relatedCourses: ['Group Projects', 'Laboratory Work', 'Capstone Project'],
      achievementLevel: 'High'
    },
    {
      id: 'PO5',
      title: 'Lifelong Learning',
      description: 'Graduates will demonstrate the ability to engage in lifelong learning and professional development.',
      relatedCourses: ['All Courses', 'Independent Study', 'Professional Development'],
      achievementLevel: 'Medium'
    }
  ];

  const outcomes = type === 'course' 
    ? courseOutcomes.filter(outcome => selectedModule === 'all' || outcome.moduleId === selectedModule || outcome.moduleId === 'all')
    : programOutcomes;

  const getAchievementBadge = (level: string) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    
    switch (level) {
      case 'High':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'Medium':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'Low':
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const title = type === 'course' ? 'Course Outcomes (CO)' : 'Program Outcomes (PO)';
  const icon = type === 'course' ? '🎯' : '🏆';

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{icon}</span>
          <h2 className="text-xl font-semibold">{title}</h2>
        </div>
        <div className="text-sm text-gray-500">
          {outcomes.length} outcomes
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {outcomes.map((outcome) => (
          <div
            key={outcome.id}
            className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden"
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                    <span className="text-blue-600 dark:text-blue-400 font-bold text-sm">
                      {outcome.id}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {outcome.title}
                    </h3>
                    <span className={getAchievementBadge(outcome.achievementLevel)}>
                      {outcome.achievementLevel} Achievement
                    </span>
                  </div>
                </div>
              </div>

              <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                {outcome.description}
              </p>

              {type === 'course' && 'assessmentMethods' in outcome && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Assessment Methods:
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {outcome.assessmentMethods.map((method, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                      >
                        {method}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {type === 'program' && 'relatedCourses' in outcome && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Related Courses:
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {outcome.relatedCourses.map((course, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                      >
                        {course}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}

        {outcomes.length === 0 && (
          <div className="text-center py-12">
            <span className="text-6xl mb-4 block">{icon}</span>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No outcomes found
            </h3>
            <p className="text-gray-500">
              No {type} outcomes available for the selected module.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OutcomesView;
